<script setup lang="ts">
import { useTranslations } from '@/composables/useTranslations';
import { ChevronRightIcon } from '@heroicons/vue/24/outline';
import { usePage } from '@inertiajs/vue3';
import { computed, ref, watch } from 'vue';

const { t, locale } = useTranslations();
const page = usePage();

interface QuestionItem {
    id: number;
    question: string;
    answer: string;
    isOpen: boolean;
}

// Récupération dynamique des données
const questionsData = computed(() => {
    const translations = (page.props as any).translations || {};
    const currentTranslations = translations[locale.value] || translations.en || {};
    return currentTranslations.faq?.questions || [];
});

const questions = ref<QuestionItem[]>([]);

// Initialisation et mise à jour lors du changement de langue
watch(
    questionsData,
    (newData) => {
        questions.value = newData.map((q: any, index: number) => ({
            id: index + 1,
            question: q.question || t('faq.questionFallback'),
            answer: q.answer || '',
            isOpen: questions.value[index]?.isOpen || false,
        }));
    },
    { immediate: true },
);

function toggleQuestion(id: number) {
    questions.value.forEach((q) => {
        if (q.id === id) q.isOpen = !q.isOpen;
        else q.isOpen = false;
    });
}

// Hooks d'animation pour une hauteur fluide
const beforeEnter = (el: any) => {
    el.style.height = '0';
};
const enter = (el: any) => {
    el.style.height = el.scrollHeight + 'px';
};
const leave = (el: any) => {
    el.style.height = '0';
};
</script>

<template>
    <section class="mx-auto w-full max-w-4xl space-y-6 rounded-3xl bg-primary-light p-8 px-4 shadow-lg sm:px-6 lg:px-8">
        <div class="mx-auto max-w-4xl">
            <h2 class="mb-12 text-center text-4xl font-extrabold text-dark">{{ t('faq.title') }}</h2>

            <div class="space-y-5">
                <div
                    v-for="q in questions"
                    :key="q.id"
                    @click="toggleQuestion(q.id)"
                    class="faq-card group cursor-pointer bg-white transition-all duration-300"
                    :class="[q.isOpen ? 'is-open' : 'is-closed']"
                >
                    <div class="flex items-center justify-between p-6 md:p-8">
                        <span class="text-xl font-semibold text-black transition-colors group-hover:text-primary">
                            {{ q.question }}
                        </span>
                        <div class="ml-4 flex-shrink-0">
                            <ChevronRightIcon :class="['h-7 w-7 text-black transition-transform duration-300', q.isOpen ? 'rotate-90' : '']" />
                        </div>
                    </div>

                    <transition name="expand" @before-enter="beforeEnter" @enter="enter" @leave="leave">
                        <div v-show="q.isOpen" class="overflow-hidden transition-all duration-300 ease-in-out">
                            <div class="px-8 pt-2 pb-8 text-lg leading-relaxed text-gray-600">
                                <div class="mb-6 h-[1px] w-full bg-gray-300"></div>
                                {{ q.answer }}
                            </div>
                        </div>
                    </transition>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.faq-card {
    /* Arrondi de 20px comme sur ton design */
    border-radius: 20px;
    /* Ombre portée douce */
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.05);
    border: 1px solid transparent;
}

.faq-card.is-open {
    box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
}

.faq-card:hover {
    border-color: #3b82f6; /* Remplace par ta couleur primary */
}

/* Classes pour la transition de hauteur */
.expand-enter-active,
.expand-leave-active {
    transition:
        height 0.35s cubic-bezier(0.4, 0, 0.2, 1),
        opacity 0.3s ease;
}

.expand-enter-from,
.expand-leave-to {
    opacity: 0;
}
</style>
